import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ThemeMode } from "../../theme/types";

interface ThemeState {
  /** The user's raw preference. Resolution of 'system' happens in ThemeProvider. */
  preference: ThemeMode;
}

const THEME_STORAGE_KEY = "themePreference";

const isValidThemePreference = (value: unknown): value is ThemeMode =>
  value === "light" || value === "dark" || value === "system";

// Helper function to load the saved theme preference from storage (both web and mobile).
// Mirrors the pattern used in uiPreferencesSlice.ts for selectedLocation.
const loadSavedThemePreference = async (): Promise<ThemeMode | null> => {
  try {
    // Check if running on web (has window and localStorage)
    if (typeof window !== "undefined" && window.localStorage) {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return isValidThemePreference(saved) ? saved : null;
    }
    // Running on React Native (iOS/Android)
    else {
      const saved = await AsyncStorage.getItem(THEME_STORAGE_KEY);
      return isValidThemePreference(saved) ? saved : null;
    }
  } catch (error) {
    console.warn("Failed to load saved theme preference from storage:", error);
    return null;
  }
};

// Helper function to save the theme preference to storage (both web and mobile).
const saveThemePreferenceToStorage = async (preference: ThemeMode): Promise<void> => {
  try {
    // Check if running on web (has window and localStorage)
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
    // Running on React Native (iOS/Android)
    else {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch (error) {
    console.warn("Failed to save theme preference to storage:", error);
  }
};

const initialState: ThemeState = {
  // Defaults to 'system' until any previously saved preference is loaded on app start.
  preference: "light",
};

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setThemePreference: (state, action: PayloadAction<ThemeMode>) => {
      state.preference = action.payload;
      // Save to storage whenever the preference changes
      saveThemePreferenceToStorage(action.payload);
    },
    loadThemePreference: (state, action: PayloadAction<ThemeMode>) => {
      state.preference = action.payload;
    },
  },
});

export const { setThemePreference, loadThemePreference } = themeSlice.actions;

// Export function to initialize the saved theme preference on app start
export const initializeSavedThemePreference = () => async (dispatch: any) => {
  const savedPreference = await loadSavedThemePreference();
  if (savedPreference) {
    dispatch(loadThemePreference(savedPreference));
  }
};

export default themeSlice.reducer;
